// The app's own concept of an activity event. Field names and the category
// vocabulary are OURS, not ACE's — the mapper (ace-activity.mapper.ts) translates
// ACE's wire shape into this. Components depend on this model and never on the DTO.

export enum ActivityCategory {
  Task = 'Task',
  Fund = 'Fund',
  Request = 'Request',
  Calendar = 'Calendar',
  Team = 'Team',
  Chat = 'Chat',
  Documents = 'Documents',
  // Any category ACE introduces that we don't yet recognise.
  Other = 'Other',
}

export interface ActivityEntry {
  id: string;
  category: ActivityCategory;
  // Raw ACE event type (e.g. TASK_CREATED). Kept for completeness; the UI shows
  // `title`, not this.
  type: string;
  title: string;
  // Usually the fund name/code.
  subtitle?: string;
  status?: string;
  progress?: number;
  // ISO 8601 UTC — the timestamp used for "last activity". Rendered via the
  // shared `timeago` pipe.
  occurredAt: string | null;
  actorName?: string;
  actorAvatarUrl?: string | null;
  // Source-app deep link from the activity target, e.g. /tasks/<id>.
  targetRoute?: string;
}

// A day-bucket of entries (Today / Yesterday / a date), as returned by ACE's
// `groups`.
export interface ActivityGroupView {
  key: string;
  title: string;
  entries: ActivityEntry[];
}

export interface ActivityFeed {
  groups: ActivityGroupView[];
  items: ActivityEntry[];
  nextCursor: string | null;
}

// Human-facing label for a category. The single place that decides how each
// category reads in the UI — independent of whatever string ACE sends.
const CATEGORY_LABELS: Record<ActivityCategory, string> = {
  [ActivityCategory.Task]: 'Task',
  [ActivityCategory.Fund]: 'Fund',
  [ActivityCategory.Request]: 'Request',
  [ActivityCategory.Calendar]: 'Calendar',
  [ActivityCategory.Team]: 'Team',
  [ActivityCategory.Chat]: 'Chat',
  [ActivityCategory.Documents]: 'Documents',
  [ActivityCategory.Other]: 'Activity',
};

export function categoryLabel(category: ActivityCategory): string {
  return CATEGORY_LABELS[category] ?? CATEGORY_LABELS[ActivityCategory.Other];
}

// An empty feed, used to degrade gracefully when activity can't be loaded.
export const EMPTY_ACTIVITY_FEED: ActivityFeed = {
  groups: [],
  items: [],
  nextCursor: null,
};
